const BRAND_GRADIENTS = [
  'from-blue-600 via-indigo-600 to-violet-700',
  'from-orange-500 via-red-500 to-rose-600',
  'from-amber-500 via-yellow-600 to-orange-700',
  'from-cyan-500 via-sky-500 to-blue-600',
  'from-red-600 via-rose-600 to-red-800',
  'from-emerald-600 via-green-600 to-teal-700',
  'from-purple-600 via-violet-600 to-fuchsia-700',
  'from-slate-600 via-gray-700 to-zinc-800',
]

export function getBrandInitials(name) {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) {
    return '?'
  }

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase()
  }

  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
}

export function getBrandGradient(brandId) {
  return BRAND_GRADIENTS[brandId % BRAND_GRADIENTS.length]
}

export function slugifyBrandName(name) {
  return name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export const FALLBACK_CATEGORY_SLUG = 'produk'

export function getBrandCategorySlug(brand, category) {
  const name = category?.name ?? brand.categoryName
  const slug = name ? slugifyBrandName(name) : ''
  return slug || FALLBACK_CATEGORY_SLUG
}

export function getBrandPath(brand, category) {
  const slug = brand.slug ?? slugifyBrandName(brand.name)
  const location = {
    name: 'game-detail',
    params: {
      category: getBrandCategorySlug(brand, category),
      name: slug,
    },
  }

  const categoryId = category?.id ?? brand.categoryId
  if (categoryId != null) {
    location.query = { category_id: String(categoryId) }
  }

  return location
}

export const CUSTOM_CATEGORY_PREFIX = 'custom:'

export function getCustomCategoryId(categoryId) {
  return `${CUSTOM_CATEGORY_PREFIX}${categoryId}`
}

export function isCustomCategoryId(categoryId) {
  return typeof categoryId === 'string' && categoryId.startsWith(CUSTOM_CATEGORY_PREFIX)
}

export function getCustomCategoryGroupId(category) {
  if (category?.custom_category_id != null) {
    return category.custom_category_id
  }

  if (category?.id != null) {
    return category.id
  }

  return null
}

export function mapWebGameBrandToGameBrand(brand, options) {
  return {
    id: brand.id,
    name: brand.name,
    slug: slugifyBrandName(brand.name),
    logoUrl: brand.image_url || undefined,
    categoryId: options?.category?.id,
    categoryName: options?.category?.name,
    customCategoryKey: options?.customCategoryKey,
    iconLabel: getBrandInitials(brand.name),
    bannerGradient: getBrandGradient(brand.id),
  }
}

export function mapCatalogItems(items) {
  return items.map((item) =>
    mapWebGameBrandToGameBrand(item.brand, { category: item.category }),
  )
}

export function mapCustomCategoryItems(items) {
  return (items ?? [])
    .filter((item) => item?.brand)
    .map((item) => {
      const groupId = getCustomCategoryGroupId(item.category)

      return mapWebGameBrandToGameBrand(item.brand, {
        category: item.category
          ? {
              id: item.category.id,
              name: item.category.name,
            }
          : undefined,
        customCategoryKey: groupId != null
          ? getCustomCategoryId(groupId)
          : undefined,
      })
    })
}

export function dedupeBrandsById(brands) {
  const brandMap = new Map()

  for (const brand of brands) {
    if (!brandMap.has(brand.id)) {
      brandMap.set(brand.id, brand)
    }
  }

  return Array.from(brandMap.values())
}

export function extractCategories(items) {
  const categoryMap = new Map()

  for (const item of items) {
    categoryMap.set(item.category.id, {
      id: item.category.id,
      name: item.category.name,
    })
  }

  return Array.from(categoryMap.values()).sort((a, b) => a.id - b.id)
}

export function groupBrandsByCategory(brands) {
  const groups = new Map()

  for (const brand of brands ?? []) {
    const id = brand.categoryId
    if (id == null) {
      continue
    }

    if (!groups.has(id)) {
      groups.set(id, {
        id,
        name: brand.categoryName || 'Katalog',
        brands: [],
      })
    }

    groups.get(id).brands.push(brand)
  }

  return Array.from(groups.values()).sort((a, b) => {
    const left = Number(a.id)
    const right = Number(b.id)
    if (Number.isFinite(left) && Number.isFinite(right)) {
      return left - right
    }

    return String(a.id).localeCompare(String(b.id))
  })
}

export function groupBrandsByCustomCategory(brands) {
  const groups = new Map()

  for (const brand of brands ?? []) {
    const id = brand.customCategoryKey
    if (id == null) {
      continue
    }

    if (!groups.has(id)) {
      groups.set(id, {
        id,
        name: brand.categoryName || 'Katalog',
        brands: [],
      })
    }

    const group = groups.get(id)
    if (group.brands.some((item) => item.id === brand.id)) {
      continue
    }

    group.brands.push(brand)
  }

  return Array.from(groups.values())
}

export function extractCustomCategories(items) {
  const categoryMap = new Map()

  for (const item of items ?? []) {
    const category = item?.category
    const groupId = getCustomCategoryGroupId(category)
    if (groupId == null) {
      continue
    }

    const id = getCustomCategoryId(groupId)
    if (!categoryMap.has(id)) {
      categoryMap.set(id, {
        id,
        name: category.name,
        source: 'custom',
      })
    }
  }

  return Array.from(categoryMap.values())
}

export function orderHomeCategories(categories) {
  const custom = []
  const rest = []

  for (const category of categories) {
    if (category.source === 'custom' || isCustomCategoryId(category.id)) {
      custom.push(category)
    } else {
      rest.push(category)
    }
  }

  return [...custom, ...rest]
}
